const DEFAULT_TIMEOUT_MS = 30_000;

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type FetchArguments = Parameters<FetchLike>;

const isKeepAliveRequest = (input: FetchArguments[0], init?: FetchArguments[1]) => {
  if (init?.keepalive) {
    return true;
  }
  if (input instanceof Request && input.keepalive) {
    return true;
  }
  return false;
};

/**
 * Wraps the native fetch with a timeout-aware AbortController.
 * - Respects existing AbortSignals (if the caller provided one, we assume they manage cancellation).
 * - Skips keepalive requests so the browser can complete the upload even after a navigation.
 * - Avoids cloning the Request object to prevent double-reading bodies (critical for streaming/batched requests).
 */
export async function fetchWithTimeout(
  input: FetchArguments[0],
  init?: FetchArguments[1],
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  fetchImpl: FetchLike = fetch
): Promise<Response> {
  if (init?.signal || isKeepAliveRequest(input, init)) {
    return fetchImpl(input, init ?? {});
  }

  const controller = new AbortController();
  // Negative or non-finite durations bubble up as Node TimeoutNegativeWarning logs in dev,
  // so normalize here once instead of trusting every caller to sanitize deadlines.
  const normalizedTimeoutMs = Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_TIMEOUT_MS;
  const clampedTimeoutMs = Math.max(0, normalizedTimeoutMs);

  const timer = setTimeout(() => {
    if (typeof DOMException === 'function') {
      controller.abort(new DOMException('Request timed out', 'AbortError'));
      return;
    }
    controller.abort();
  }, clampedTimeoutMs);

  try {
    const finalInit: RequestInit = {
      ...init,
      signal: controller.signal,
    };
    return await fetchImpl(input, finalInit);
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      const timeoutError = new Error(`Fetch aborted after ${clampedTimeoutMs}ms`);
      timeoutError.name = 'FetchTimeoutError';
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export const fetchTimeout = fetchWithTimeout;

export const DEFAULT_FETCH_TIMEOUT_MS = DEFAULT_TIMEOUT_MS;
